import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

// Server: social/SocialAccountDto.java — the shared row shape for every friend/follow listing
// endpoint (friends, incoming/outgoing pending, blocked, following): the OTHER account plus
// whatever timestamp is meaningful for that list (responded_at for accepted friendships,
// created_at for pending/blocked/follow rows).
export type SocialAccountDto = {
  accountId: string;
  displayName: string;
  since: string;
};

// Server: social/FriendshipActionResultDto.java — returned by every FriendshipService mutation:
// the OTHER account, and the resulting status ("pending"/"accepted"/"removed"/"blocked"/
// "unblocked").
export type FriendshipActionResultDto = {
  accountId: string;
  displayName: string;
  status: string;
};

// Both friends.ts and follows.ts's own server-side split (social/FriendController.java +
// social/FollowController.java) live under this one client module — the brief names it
// "friends.ts" singular, and both are the same social-graph domain from the client's point of
// view. Everything shares one 'friends' key prefix so a single invalidateQueries({queryKey:
// friendsKeys.all}) after any mutation here catches every list below (TanStack's prefix match).
export const friendsKeys = {
  all: ['friends'] as const,
  list: ['friends', 'list'] as const,
  incoming: ['friends', 'requests', 'incoming'] as const,
  outgoing: ['friends', 'requests', 'outgoing'] as const,
  blocked: ['friends', 'blocked'] as const,
  following: ['friends', 'following'] as const,
};

export const listFriends = () => apiFetch<SocialAccountDto[]>('/friends');
export const listIncomingRequests = () => apiFetch<SocialAccountDto[]>('/friends/requests/incoming');
export const listOutgoingRequests = () => apiFetch<SocialAccountDto[]>('/friends/requests/outgoing');
export const listBlocked = () => apiFetch<SocialAccountDto[]>('/friends/blocked');
export const listFollowing = () => apiFetch<SocialAccountDto[]>('/follows');

export function useFriends(enabled = true) {
  return useQuery({ queryKey: friendsKeys.list, queryFn: listFriends, enabled });
}
export function useIncomingRequests() {
  return useQuery({ queryKey: friendsKeys.incoming, queryFn: listIncomingRequests });
}
export function useOutgoingRequests() {
  return useQuery({ queryKey: friendsKeys.outgoing, queryFn: listOutgoingRequests });
}
export function useBlocked() {
  return useQuery({ queryKey: friendsKeys.blocked, queryFn: listBlocked });
}
export function useFollowing() {
  return useQuery({ queryKey: friendsKeys.following, queryFn: listFollowing });
}

// POST /friends/requests {handle} — FriendController.HandleRequest. Refusals: not_found (404,
// SocialNotFoundException — same opaque shape an unknown handle OR a blocking account both get,
// R3's block-opacity ruling), self_request (400), already_friends (409), already_pending (409),
// friend_request_cap (409, 20 outstanding).
export const sendFriendRequest = (handle: string) =>
  apiFetch<FriendshipActionResultDto>('/friends/requests', { method: 'POST', body: JSON.stringify({ handle }) });

export const acceptFriendRequest = (accountId: string) =>
  apiFetch<FriendshipActionResultDto>(`/friends/requests/${accountId}/accept`, { method: 'POST' });
export const declineFriendRequest = (accountId: string) =>
  apiFetch<void>(`/friends/requests/${accountId}/decline`, { method: 'POST' });
export const cancelFriendRequest = (accountId: string) =>
  apiFetch<void>(`/friends/requests/${accountId}`, { method: 'DELETE' });
export const removeFriend = (accountId: string) =>
  apiFetch<void>(`/friends/${accountId}`, { method: 'DELETE' });
export const blockAccount = (accountId: string) =>
  apiFetch<void>(`/friends/${accountId}/block`, { method: 'POST' });
export const unblockAccount = (accountId: string) =>
  apiFetch<void>(`/friends/${accountId}/block`, { method: 'DELETE' });

// Server: FollowController's `/follows/{displayName}` — keyed by display name, not accountId
// (unlike every friend endpoint above), matching the controller exactly.
export const followAccount = (displayName: string) =>
  apiFetch<void>(`/follows/${encodeURIComponent(displayName)}`, { method: 'POST' });
export const unfollowAccount = (displayName: string) =>
  apiFetch<void>(`/follows/${encodeURIComponent(displayName)}`, { method: 'DELETE' });

// None of these mutations move item/Shell value (pure social-graph edges), so — unlike
// gifts/trades/guild-bank/stalls/auctions — none of them take an idempotencyKey; a doubled
// request just re-applies the same idempotent DB state (a second `request()` on an existing
// pending row 409s as already_pending rather than silently duplicating). Every mutation here
// invalidates the whole friendsKeys.all prefix on success — cheap, infrequent actions where a
// broad refetch is simpler than hand-picking which of the 6 lists a given action could affect
// (e.g. `accept` moves a row from incoming to the friends list AND clears it from outgoing on
// the other side; `block` can affect the friends list, both pending lists, AND the blocked list
// at once).
function useFriendsMutation<TArgs, TData>(mutationFn: (args: TArgs) => Promise<TData>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: friendsKeys.all }),
  });
}

export function useSendFriendRequest() { return useFriendsMutation(sendFriendRequest); }
export function useAcceptFriendRequest() { return useFriendsMutation(acceptFriendRequest); }
export function useDeclineFriendRequest() { return useFriendsMutation(declineFriendRequest); }
export function useCancelFriendRequest() { return useFriendsMutation(cancelFriendRequest); }
export function useRemoveFriend() { return useFriendsMutation(removeFriend); }
export function useBlockAccount() { return useFriendsMutation(blockAccount); }
export function useUnblockAccount() { return useFriendsMutation(unblockAccount); }
export function useFollowAccount() { return useFriendsMutation(followAccount); }
export function useUnfollowAccount() { return useFriendsMutation(unfollowAccount); }
